export interface ProjectNavChild {
  label: string;
  /** Path template with `:projectId` placeholder */
  path: string;
  id: string;
  /** "react" uses client-side routing; "rails" does a full-page GET */
  routing: "react" | "rails";
  /**
   * When set, the child is only shown if the project's status is one of these
   * values. When omitted, the child is always shown.
   */
  visibleForStatuses?: string[];
  /**
   * When set, the child is only shown if the account has the PSA capability
   * enabled -- or if this project was already pushed to a PSA, so that a
   * project pushed before the capability was turned off keeps its link.
   */
  requiresPsa?: boolean;
  /**
   * When set, the child is only shown if the user can see change orders (or
   * would see the roles hint for them); see useChangeOrderAccess.
   */
  requiresChangeOrders?: boolean;
}

export interface ProjectNavSection {
  label: string;
  id: string;
  children: ProjectNavChild[];
}

const projectNavItems: ProjectNavSection[] = [
  {
    label: "Project Details",
    id: "project-details",
    children: [
      {
        label: "Project Overview",
        path: "/projects/:projectId/edit",
        id: "project-overview",
        routing: "react",
      },
      {
        label: "Customer Summary",
        path: "/projects/:projectId/sow_outcome/edit",
        id: "project-summary",
        routing: "react",
      },
      {
        label: "Service Locations",
        path: "/projects/:projectId/project_locations",
        id: "project-locations",
        routing: "react",
      },
      {
        label: "Contacts",
        path: "/projects/:projectId/project_contacts",
        id: "project-contacts",
        routing: "react",
      },
      {
        label: "Terms & Conditions",
        path: "/projects/:projectId/project_conditions",
        id: "project-terms-and-conditions",
        routing: "react",
      },
      {
        label: "Teammates",
        path: "/projects/:projectId/project_collaborators",
        id: "project-collaborators",
        routing: "react",
      },
      {
        label: "Attachments",
        path: "/projects/:projectId/attachments",
        id: "project-attachments",
        routing: "react",
      },
      {
        label: "PSA Project",
        path: "/projects/:projectId/psa_project",
        id: "project-psa-project",
        routing: "react",
        visibleForStatuses: ["approved", "won"],
        requiresPsa: true,
      },
    ],
  },
  // Customer Success menu hidden for now — restore this section to bring back
  // Outcomes and KPIs, Ongoing Strategy, and Adoption Barriers.
  // {
  //   label: "Customer Success",
  //   id: "customer-success-item",
  //   children: [
  //     {
  //       label: "Outcomes and KPIs",
  //       path: "/projects/:projectId/customer_successes",
  //       id: "project-outcomes",
  //       routing: "react",
  //     },
  //     {
  //       label: "Ongoing Strategy",
  //       path: "/projects/:projectId/success_strategies",
  //       id: "project-strategies",
  //       routing: "rails",
  //     },
  //     {
  //       label: "Adoption Barriers",
  //       path: "/projects/:projectId/adoption_barriers",
  //       id: "project-barriers",
  //       routing: "rails",
  //     },
  //   ],
  // },
  {
    label: "Services",
    id: "service-item",
    children: [
      {
        label: "Professional Services",
        path: "/projects/:projectId/project_editor",
        id: "ps-services",
        routing: "react",
      },
      {
        label: "Managed Services",
        path: "/projects/:projectId/managed_service",
        id: "ms-services",
        routing: "react",
      },
      {
        label: "Vendor Quotes",
        path: "/projects/:projectId/quotes",
        id: "tp-vendor-quotes",
        routing: "react",
      },
      {
        label: "Governance",
        path: "/projects/:projectId/project_governances",
        id: "project-governance",
        routing: "react",
      },
      {
        label: "Surveys",
        path: "/projects/:projectId/project_surveys",
        id: "project-surveys",
        routing: "react",
      },
    ],
  },
  {
    label: "Product/Expense",
    id: "project-material-item",
    children: [
      {
        label: "Product",
        path: "/projects/:projectId/project_materials",
        id: "sow-materials",
        routing: "react",
      },
      {
        label: "Travel/Expense",
        path: "/projects/:projectId/project_expenses",
        id: "project-expense",
        routing: "react",
      },
    ],
  },
  {
    label: "Resources",
    id: "project-resource-item",
    children: [
      {
        label: "Resources",
        path: "/projects/:projectId/project_resources",
        id: "project-resources",
        routing: "react",
      },
      {
        label: "Resource Planner",
        path: "/projects/:projectId/resource_plans",
        id: "project-resource-planner",
        routing: "react",
      },
    ],
  },
  {
    label: "Pricing",
    id: "project-pricing-item",
    children: [
      {
        label: "Overall Pricing",
        path: "/projects/:projectId/project_pricing",
        id: "project-pricing",
        routing: "react",
      },
      {
        label: "Work Breakdown",
        path: "/projects/:projectId/summary",
        id: "project-work-breakdown",
        routing: "react",
      },
      {
        label: "PS Pricing",
        path: "/projects/:projectId/sow_pricings",
        id: "ps-pricing",
        routing: "react",
      },
      {
        label: "MS Pricing",
        path: "/projects/:projectId/service_summary",
        id: "ms-pricing",
        routing: "react",
      },
      {
        label: "Third Party Pricing",
        path: "/projects/:projectId/third_party_pricing",
        id: "3ps-pricing",
        routing: "react",
      },
      {
        label: "Product Pricing",
        path: "/projects/:projectId/project_material_pricings",
        id: "matl-pricing",
        routing: "react",
      },
      {
        label: "Payment Credits",
        path: "/projects/:projectId/project_credits",
        id: "project-credits",
        routing: "react",
      },
    ],
  },
  {
    label: "Approvals",
    id: "approval-item",
    children: [
      {
        label: "Approval Requests",
        path: "/projects/:projectId/project_approvals",
        id: "project-approvals",
        routing: "react",
      },
      {
        label: "Versions",
        path: "/projects/:projectId/project_versions",
        id: "project-versions",
        routing: "react",
      },
      {
        label: "Change Orders",
        path: "/projects/:projectId/change_orders",
        id: "project-change-orders",
        routing: "react",
        requiresChangeOrders: true,
      },
      {
        label: "Notes",
        path: "/projects/:projectId/notes",
        id: "project-notes",
        routing: "react",
      },
    ],
  },
];

export interface NavVisibility {
  /** Undefined until the project query resolves. */
  projectStatus?: string;
  /** The account has an enabled PSA integration (`psa?` on the account). */
  psaEnabled: boolean;
  /** This project already carries a psa-project record. */
  hasPsaProject: boolean;
  /** useChangeOrderAccess().navVisible; hidden when omitted. */
  changeOrdersVisible?: boolean;
}

/**
 * Applies every gate and drops sections left with no children.
 *
 * The PSA rule mirrors projects/_tabs_info.haml: an account that never enabled
 * the PSA capability gets no link, but a project already pushed keeps one, and
 * the page it opens explains why it cannot be resubmitted.
 */
export const visibleNavSections = ({
  projectStatus,
  psaEnabled,
  hasPsaProject,
  changeOrdersVisible,
}: NavVisibility): ProjectNavSection[] =>
  projectNavItems
    .map((section) => ({
      ...section,
      children: section.children.filter((child: ProjectNavChild) => {
        const statusOk =
          !child.visibleForStatuses ||
          (projectStatus !== undefined &&
            child.visibleForStatuses.includes(projectStatus));
        const psaOk = !child.requiresPsa || psaEnabled || hasPsaProject;
        const coOk =
          !child.requiresChangeOrders || Boolean(changeOrdersVisible);
        return statusOk && psaOk && coOk;
      }),
    }))
    .filter((section) => section.children.length > 0);

export const resolveProjectPath = (
  template: string,
  projectId: string
): string => {
  return template.replace(":projectId", projectId);
};

export default projectNavItems;
